import { Input, Form, DatePicker } from "@gw/web-basic-components";
import React, { CSSProperties } from "react";
import NumberInput from "../number-input";
import withRevoke from "../hoc/with-revoke";
import ReadonlyItem from "../readonly-item";
import {
  getFieldPath,
  getFieldValue,
  getFormulaValue,
  isField,
  isIndexCode,
  isListValue,
} from "./value";
import {
  Cell,
  Formula,
  Path,
  TableEditorConfig,
  TableRenderConfig,
  Value,
} from "../dto";
import { DATA_TYPE, FORMULA_TYPE, ROW, VALUE_TYPE } from "../contanst";
import { getLoopMeta, transformLoops } from "./loop";
import { isNullOrUndefined } from "../../utils";
import { generateCellRectsByGrid, generateGrid } from "./transform";
import { generateConfigByGrid } from "./table";

const InputWithRevoke = withRevoke(Input);
const TextareaWithRevoke = withRevoke(Input.Textarea);
const NumberInputWithRevoke = withRevoke(NumberInput);
const DatePickerWithRevoke = withRevoke(DatePicker);
const DateRangePickerWithRevoke = withRevoke(DatePicker.LegacyRangePicker);

const inputDefaultProps = {
  size: "small" as const,
  placeholder: "请输入",
};

export const getInitialValue = (
  dataSource: Value[],
  name: string,
  index?: number
) => {
  const data = dataSource.find((i) => i.code === name);
  if (isListValue(data)) {
    return isNullOrUndefined(index)
      ? data.valueList?.toString()
      : data.valueList?.[index];
  }
  return data?.value;
};
// 判断单元格是否可编辑
export const isEditable = (
  cell: Cell,
  dataSource: Value[],
  formulas: Formula[]
) => {
  // 必须含有type并且value只能有一个不为index的动态值
  const cellEditable =
    [DATA_TYPE.FORMAT, DATA_TYPE.DYNAMIC].includes(cell.type) &&
    (cell.value ?? []).filter((v) => isField(v.value, v.type)).length === 1;
  if (!cellEditable) {
    return false;
  }
  const field = cell.value.find((v) => isField(v.value, v.type));
  const data = dataSource.find((i) => i.code === field?.value);
  if (!data) {
    return false;
  }
  const formula = formulas.find((i) => i.code === field?.value);
  // 这个动态值不是公式或者是为自身的公式
  const formulaEditable =
    !formula ||
    formula.value.length === 0 ||
    (formula.value.length === 1 && formula.value[0].value === field.value);
  return !!data.editEnable && formulaEditable;
};

/**
 * 获取单元格的数据
 * @param cell 单元格
 * @param data 数据
 * @param index 循环index
 * @returns 单元格数据
 */
export const renderCell = (cell: Cell, data: Value[], index?: number) => {
  if (![DATA_TYPE.FORMAT, DATA_TYPE.DYNAMIC].includes(cell.type!)) {
    return "";
  }
  return cell.value?.map((item, idx) => {
    const field = data.find((d) => d.code === item.value);
    const value = getFieldValue(item, data, index);
    return (
      <ReadonlyItem
        key={idx}
        value={value}
        type={
          field?.isList && isNullOrUndefined(index)
            ? VALUE_TYPE.TEXT
            : field?.type
        }
        placeholder={cell.placeholder}
        attr={cell.attr}
      />
    );
  });
};

// 渲染单元格
export const renderCellWithEditable = ({
  cell,
  dataSource,
  editable,
  index,
  onChange,
}: {
  cell: Cell;
  dataSource: Value[];
  editable: boolean;
  index?: number;
  onChange: (path: Path) => void;
}) => {
  const { value = [], attr, placeholder } = cell;
  const path = getFieldPath(value, dataSource, index);
  if (!editable) {
    return value.map((i) => {
      if (isIndexCode(i.value) && i.type === FORMULA_TYPE.DYNAMIC) {
        return index + 1;
      }
      const field = dataSource.find((d) => d.code === i.value);
      if (i.type === FORMULA_TYPE.DYNAMIC) {
        return (
          <Form.Item noStyle name={path}>
            <ReadonlyItem
              initialValue={getInitialValue(dataSource, i.value, index)}
              type={field?.type}
              attr={attr}
              placeholder={placeholder}
              showChanged
            />
          </Form.Item>
        );
      }
      return i.value;
    });
  }
  const name = value.find((i) => isField(i.value, i.type)).value;
  const field = dataSource.find((i) => i.code === name);
  const initialValue = getInitialValue(dataSource, name, index);
  const commonProps = {
    className: "wbuc-settlement-cell-control",
    initialValue,
  };
  let type = "text";
  if (field?.type === VALUE_TYPE.NUMBER) {
    type = "number";
    // @ts-ignore
  } else if (cell.style?.textWrap === "wrap") {
    type = "textarea";
  }
  const components = {
    text: <InputWithRevoke {...inputDefaultProps} {...commonProps} />,
    textarea: (
      <TextareaWithRevoke
        maxLength={200}
        {...inputDefaultProps}
        {...commonProps}
      />
    ),
    number: (
      <NumberInputWithRevoke
        {...inputDefaultProps}
        {...commonProps}
        precision={attr?.precision}
        rounding={attr?.rounding}
        trailingZeroes={attr?.trailingZeroes}
        thousandSeparator={attr?.thousandSeparator}
        onBlur={() => onChange?.(path)}
        onRevoke={() => onChange?.(path)}
      />
    ),
    date: <DatePickerWithRevoke placeholder="请选择" {...commonProps} simple />,
    daterange: (
      <DateRangePickerWithRevoke
        placeholder={["请选择", "请选择"]}
        {...commonProps}
        size="small"
      />
    ),
  };

  return (
    <Form.Item name={path} noStyle rules={[{ required: !!field?.required }]}>
      {components[type]}
    </Form.Item>
  );
};
// 表单值转换为数据源
export const transformFormValues = (
  formValues: Record<string, any>,
  dataSource: Value[]
) => {
  return Object.entries(formValues).reduce((acc, [key, value]) => {
    const originData = dataSource.find((d) => d.code === key);
    if (originData) {
      acc.push({
        ...originData,
        [originData.isList ? "valueList" : "value"]: value,
      });
    }
    return acc;
  }, [] as Value[]);
};
// 数据源生成表单值
export const generateFormValues = (
  dataSource: Value[],
  config: TableEditorConfig
) => {
  const values: Record<string, any> = {};
  const withFormulasDataSource = generateDataSourceByFormulas(
    dataSource,
    config.formulas
  );
  for (const data of withFormulasDataSource) {
    values[data.code] = isListValue(data) ? data.valueList : data.value;
  }
  return values;
};
// 根据公式生成数据源
export const generateDataSourceByFormulas = (
  dataSource: Value[],
  formulas: Formula[]
) => {
  const formulasData: Value[] = [];
  // dataSource没有公式的字段则自己计算生成
  formulas.forEach((f) => {
    if (!dataSource.find((d) => d.code === f.code)) {
      const value = getFormulaValue(f.value, dataSource);
      formulasData.push({
        code: f.code,
        isList: f.isList ?? Array.isArray(value),
        type: VALUE_TYPE.NUMBER,
        value,
        editEnable: f.value.length === 1 && f.value[0].value === f.code,
      } as Value);
    }
  });
  return [...dataSource, ...formulasData];
};

/**
 * 生成渲染（预览）的表格配置
 * @param config 表格配置
 * @param dataSource 表格数据
 * @returns 表格配置
 */
export const generateRenderConfig = (
  config: TableEditorConfig,
  dataSource: Value[]
): TableRenderConfig => {
  // 转换循环配置
  const loops = transformLoops(config.loops);
  // 原始矩阵
  const grid = generateGrid(config);
  // 原始单元格位置
  const rectsMap = generateCellRectsByGrid(grid);
  // 新单元格配置
  const cellsMap: Record<string, Cell> = {};
  // 新行配置
  const rowsConfig: Partial<
    Omit<TableRenderConfig["rows"][number], "cells">
  >[] = [];
  // 构建使用的新矩阵
  const newGrid = [...grid];
  // 插入的循环数量
  let insertLength = 0;
  // 用于设置一个单元格跨循环的索引（此情况需要分割此单元格来用于不同循环）
  const rowSpanLoopIdsMap: Record<string, number> = {};
  for (const row of config.rows) {
    for (const cell of row.cells) {
      cellsMap[cell.id] = cell;
    }
  }
  for (const loop of loops) {
    const { id: loopId, minRow, maxRow } = loop;
    const { hasLoopField, loopLength, bindCodes } = getLoopMeta(
      config.rows,
      loop,
      dataSource
    );
    if (!hasLoopField) continue;
    // 当前循环已经设置过的跨循环单元格
    const setRowSpanLoopIds = new Set<string>();
    // 循环行原始长度
    const rowLength = maxRow - minRow + 1;
    // 循环原矩阵
    const segment = grid.slice(minRow, maxRow + 1);
    // 替换的循环新行
    const rows: string[][] = new Array(loopLength * rowLength);
    let rowOffset = 0;
    for (let idx = 0; idx < loopLength; idx++) {
      for (let r = 0; r < rowLength; r++) {
        const rowCells = segment[r];
        const newRow: string[] = new Array(rowCells.length);
        for (let c = 0; c < rowCells.length; c++) {
          const id = rowCells[c];
          const rect = rectsMap[id];
          const cell = cellsMap[id];
          // 当前单元格刚好撑满循环行
          const isEquals = minRow === rect.minRow && maxRow === rect.maxRow;
          // 当前单元格包含循环行
          const isContains =
            rect.minRow <= minRow && rect.maxRow >= maxRow && !isEquals;
          // 当前单元格与循环行交叉
          const isIntersected = rect.minRow < minRow || rect.maxRow > maxRow;
          // 当前单元格是否为循环字段
          const isLoopField = cell.value?.some(
            (v) =>
              v.type === FORMULA_TYPE.DYNAMIC && bindCodes.includes(v.value)
          );
          // 是否可合并（设置了自动合并&撑满循环行&不是循环字段）
          const mergeable = cell.autoMerge && isEquals && !isLoopField;
          // 可合并或者包含切不是循环字段则合并单元格
          if (mergeable || (isContains && !isLoopField)) {
            newRow[c] = id;
          } else {
            let newId = `${id}_${idx}`;
            // 出现交叉并且是循环字段则说明有可能同一个单元格设置了多个循环
            if (isIntersected && isLoopField) {
              // 如果已经出现此情况则分割单元格
              if (rowSpanLoopIdsMap[id]) {
                newId = `${id}_${rowSpanLoopIdsMap[id]}_${idx}`;
              }
              // 如果当前循环未出现此情况则标记
              if (!setRowSpanLoopIds.has(id)) {
                setRowSpanLoopIds.add(id);
              }
            }
            newRow[c] = newId;
            // 保证循环生成的单元格配置不变
            cellsMap[newId] = cellsMap[id];
          }
        }
        rows[rowOffset++] = newRow;
      }
    }
    // 设置跨循环单元格索引
    setRowSpanLoopIds.forEach(
      (id) => (rowSpanLoopIdsMap[id] = (rowSpanLoopIdsMap[id] ?? 0) + 1)
    );
    for (let i = 0; i < rows.length; i++) {
      const loopIdx = Math.floor(i / rowLength);
      const originRowId = config.rows[minRow + (i % rowLength)].id;
      // 设置行循环配置
      rowsConfig[minRow + i + insertLength] = {
        loop: { id: loopId, index: loopIdx },
        id: `${originRowId}_${loopIdx}`,
      };
    }
    // 替换矩阵
    newGrid.splice(minRow + insertLength, rowLength, ...rows);
    insertLength += rows.length - rowLength;
  }
  return generateConfigByGrid(config, newGrid, cellsMap, rowsConfig);
};

/**
 * 根据单元格获取样式
 */
export const getStyleByCell = (style: Cell["style"]): CSSProperties => {
  const { border, ...rest } = style ?? {};
  return {
    ...rest,
    ...border,
    // @ts-ignore
    ...(rest.textWrap === "wrap" ? { wordBreak: "break-word" } : {}),
    // 用于自适应单元格宽度
    width: 0,
  };
};

/**
 * 根据单元格获取尺寸
 * @param cell 单元格
 * @param config 表格配置
 * @returns 单元格尺寸
 */
export const getCellSize = (
  cell: Pick<Cell, "sizeMode" | "rect">,
  config: TableRenderConfig
) => {
  const { sizeMode = "auto", rect } = cell;
  if (sizeMode === "auto" || !rect) {
    return {};
  }
  const rows = config.rows.map((i) => i.height ?? ROW.DEFAAULT_HEIGHT);
  const { cols } = config;
  const { minRow, maxRow, minCol, maxCol } = rect;
  const height = rows.slice(minRow, maxRow + 1).reduce((a, b) => a + b, 0);
  const width = cols.slice(minCol, maxCol + 1).reduce((a, b) => a + b, 0);
  return { height, width };
};
